/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { Alert } from "@rewards-web/shared/components/alert";
import { PageLoadingState } from "@rewards-web/shared/components/page-loading-state";
import { serializeUrl } from "@rewards-web/shared/lib/serialize-url";
import { useTrack } from "@rewards-web/shared/modules/analytics";
import { reportError } from "@rewards-web/shared/modules/error";
import { useSnackbar } from "@rewards-web/shared/modules/snackbar";
import { ApolloGraphQLResponseContext } from "@rewards-web/shared/types/apollo-response-context";

import { PageCard } from "../../../../../../shared/components/page-card";
import { PageCardTitle } from "../../../../../../shared/components/page-card/page-card-title";
import { useEmailCommunicationCardDataQuery } from "./email-communication-card-data.generated";
import {
  SettingsEmailCommunicationForm,
  SettingsEmailCommunicationFormValues,
} from "./email-communication-form";
import { useUpdateEmailCommunicationSettingsMutation } from "./update-email-communication-settings.generated";

export function SettingsEmailCommunicationCard(): JSX.Element {
  const track = useTrack();
  const snackbar = useSnackbar();
  const emailCommunicationCardDataQuery = useEmailCommunicationCardDataQuery({
    fetchPolicy: "network-only",
    onError: reportError,
  });

  const [
    updateEmailCommunicationSettings,
  ] = useUpdateEmailCommunicationSettingsMutation();

  const handleSubmit = async (values: SettingsEmailCommunicationFormValues) => {
    try {
      const res = await updateEmailCommunicationSettings({
        variables: {
          shortName: values.organizationShortName,
          emailBannerLogoImage:
            values.emailBannerLogoImage instanceof File
              ? values.emailBannerLogoImage
              : undefined,
          timezone: values.timezone,
          websiteUrl: values.websiteUrl
            ? serializeUrl(values.websiteUrl)
            : null,
        },
      });

      snackbar.show({
        severity: "success",
        message: "The settings have been updated.",
      });
      track("Organization settings saved", {
        settingsCard: "Email Communication",
        requestId: (res.context as ApolloGraphQLResponseContext).requestId,
      });
    } catch (error) {
      reportError(error);
      snackbar.show({
        severity: "error",
        message: "An unexpected error has occurred. Please try again later.",
      });
    }
  };

  const content = (() => {
    if (emailCommunicationCardDataQuery.error) {
      return (
        <Alert
          severity="error"
          message="An unexpected error occurred. Please try again later."
        />
      );
    }

    if (
      emailCommunicationCardDataQuery.loading ||
      !emailCommunicationCardDataQuery.data
    ) {
      return (
        <div
          css={css`
            height: 200px;
          `}
        >
          <PageLoadingState />
        </div>
      );
    }

    return (
      <SettingsEmailCommunicationForm
        data={emailCommunicationCardDataQuery.data}
        onSubmit={handleSubmit}
      />
    );
  })();

  return (
    <PageCard>
      <PageCardTitle title="Email Communication" />
      {content}
    </PageCard>
  );
}
